import { useSearchParams } from 'react-router-dom';
import { Bot, ShieldAlert, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBotProfile } from '@/lib/hooks';

/**
 * Login landing. Hero + Discord OAuth-Button + optional error banner sourced
 * from the URL query (?error=not_whitelisted / no_server / oauth_failed) — the callback
 * redirects here instead of returning raw JSON, so the flow feels intentional.
 */

const ERROR_MESSAGES: Record<
  string,
  { title: string; body: (username: string | null) => string }
> = {
  not_whitelisted: {
    title: 'Kein Zugriff',
    body: (username) =>
      username
        ? `Willkommen ${username} — aber dein Discord-Account ist nicht auf der Whitelist. Bitte einen Admin, dich freizuschalten.`
        : 'Dein Discord-Account ist nicht auf der Whitelist. Ein Admin muss dich freischalten.',
  },
  // Access model 2026-10: login needs at least one server assignment.
  no_server: {
    title: 'Noch kein Server',
    body: (username) =>
      username
        ? `Hallo ${username} — dir ist noch kein Server zugeordnet. Frag einen Admin oder Verwalter, dich hinzuzufügen.`
        : 'Dir ist noch kein Server zugeordnet. Frag einen Admin oder Verwalter, dich hinzuzufügen.',
  },
  oauth_failed: {
    title: 'Login fehlgeschlagen',
    body: () => 'Der Discord-Login konnte nicht abgeschlossen werden. Versuch es erneut oder wende dich an einen Admin.',
  },
};

const FEATURES: Array<{ icon: typeof Bot; title: string; description: string }> = [
  {
    icon: Bot,
    title: 'Bot-Steuerung',
    description: 'Warns, Bans, Timeouts, Voice-Kicks — alles im Browser statt in Discord tippen.',
  },
  {
    icon: Sparkles,
    title: 'Live-Daten',
    description: 'Voice-Belegung, Nachrichten-Rate, XP-Rangliste live aus dem Bot gestreamt.',
  },
  {
    icon: ShieldAlert,
    title: 'Fine-grained Access',
    description: 'Admins wählen pro Nutzer welche Server sichtbar sind und welche Aktionen erlaubt.',
  },
];

export function Login() {
  const [params] = useSearchParams();
  const bot = useBotProfile();
  const errorKey = params.get('error');
  const username = params.get('username');
  const errorEntry = errorKey ? ERROR_MESSAGES[errorKey] : null;

  return (
    <div className="min-h-screen">
      <div className="mx-auto flex min-h-screen max-w-6xl flex-col items-center justify-center px-4 py-12">
        <div className="grid w-full grid-cols-1 gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          {/* Left: Hero */}
          <div className="rounded-2xl border bg-card/70 p-6 text-center shadow-xl sm:p-8 lg:text-left">
            <div className="mb-6 inline-flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl bg-primary/15 text-primary lg:h-20 lg:w-20">
              {bot.data?.avatarUrl ? (
                <img src={bot.data.avatarUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <Bot className="h-8 w-8 lg:h-10 lg:w-10" />
              )}
            </div>
            <h1 className="mb-4 text-4xl font-semibold tracking-tight lg:text-5xl">
              {bot.data?.name ?? 'Dashboard'}
            </h1>
            <p className="mb-8 max-w-lg text-lg text-muted-foreground lg:mx-0 mx-auto">
              Steuere deinen Discord-Bot im Browser — Moderation, Music, Voice,
              Warnungen, Panels — alles ohne Slash-Commands.
            </p>

            <ul className="mx-auto flex max-w-lg flex-col gap-3 text-left lg:mx-0">
              {FEATURES.map((f) => (
                <li key={f.title} className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                    <f.icon className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">{f.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {f.description}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: Login card */}
          <div className="mx-auto w-full max-w-md">
            {errorEntry && (
              // Glass like the login card — red text straight on the wallpaper is unreadable.
              <div className="mb-4 flex gap-3 rounded-lg border border-destructive/60 bg-card p-4 shadow-xl">
                <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
                <div>
                  <div className="font-semibold text-red-600 dark:text-red-400">
                    {errorEntry.title}
                  </div>
                  <div className="mt-1 text-sm text-foreground/90">
                    {errorEntry.body(username)}
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-2xl border bg-card p-8 shadow-xl">
              <h2 className="mb-2 text-xl font-semibold">Anmelden</h2>
              <p className="mb-6 text-sm text-muted-foreground">
                Login läuft über Discord — kein Passwort, keine E-Mail nötig.
                Nur Accounts auf der Whitelist bekommen Zugang.
              </p>
              <Button
                size="lg"
                className="w-full gap-2 bg-[#5865F2] text-white hover:bg-[#4752C4]"
                onClick={() => {
                  window.location.href = '/api/auth/login';
                }}
              >
                <DiscordIcon className="h-5 w-5" />
                Mit Discord anmelden
              </Button>
              <p className="mt-4 text-center text-xs text-muted-foreground">
                Wir speichern nur deine Discord-ID, Namen und Avatar.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Discord's brand mark — hand-drawn SVG so we don't pull in a brand-icon package. */
function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path d="M20.317 4.492c-1.53-.69-3.17-1.2-4.885-1.49a.075.075 0 0 0-.079.036c-.21.375-.444.864-.608 1.25a18.566 18.566 0 0 0-5.487 0 12.36 12.36 0 0 0-.617-1.25.077.077 0 0 0-.079-.036A19.79 19.79 0 0 0 3.677 4.492a.07.07 0 0 0-.032.027C.533 9.093-.32 13.555.099 17.961a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 2.98.078.078 0 0 0 .084-.026 13.83 13.83 0 0 0 1.226-1.963.074.074 0 0 0-.041-.104 13.175 13.175 0 0 1-1.872-.878.075.075 0 0 1-.008-.125c.126-.093.252-.19.372-.287a.075.075 0 0 1 .078-.01c3.927 1.764 8.18 1.764 12.061 0a.075.075 0 0 1 .079.009c.12.098.246.195.372.288a.075.075 0 0 1-.006.125c-.598.344-1.22.635-1.873.877a.075.075 0 0 0-.041.105c.36.687.772 1.34 1.225 1.964a.077.077 0 0 0 .084.026 19.834 19.834 0 0 0 6.002-2.981.076.076 0 0 0 .031-.055c.5-5.094-.838-9.52-3.549-13.442a.06.06 0 0 0-.031-.028zM8.02 15.278c-1.182 0-2.157-1.069-2.157-2.38 0-1.312.956-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.311-.956 2.38-2.157 2.38zm7.975 0c-1.183 0-2.157-1.069-2.157-2.38 0-1.312.955-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.311-.946 2.38-2.157 2.38z" />
    </svg>
  );
}
